import { useNavigate } from 'react-router';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { Button } from '@/kit/Button';
import { Checkbox } from '@/kit/Checkbox';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { InputField } from '@/kit/FormField';
import { Select } from '@/kit/Select';
import { Tooltip } from '@/kit/Tooltip';
import { NotificationTextMessage } from '@/core/enums/notification';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { managementReportingService } from '@/core/services/managementReporting';
import { validateForm } from '@/features/manage/formRules';
import {
  PACK_SECTIONS,
  type DimensionRow,
  type ManagementPackRow,
  type ManagementPeriodRow,
} from '@/core/models/management-reporting.models';
import {
  confirmDelete,
  formatDate,
  guardClient,
  MatButton,
  RequiredWrap,
  useLiveState,
  useOnInit,
  useSelectedClient,
} from './shared';
import './ManagementPacks.scss';

interface PackForm {
  id: string | null;
  name: string;
  fromManagementPeriodId: string | null;
  toManagementPeriodId: string | null;
  showPrior: boolean;
  showBudget: boolean;
  showVariance: boolean;
  /** A plain text field: a string once it has been edited; coerced with `Number(x) || 0` on save. */
  multiYearCount: number | string;
  dimensionCode: string | null;
  sections: string[];
}

interface State {
  dataSource: ManagementPackRow[];
  periods: ManagementPeriodRow[];
  dimensions: DimensionRow[];
  showForm: boolean;
  form: PackForm;
  /** A failed Save marks the missing required fields red. */
  tried: boolean;
  /** True while Save is running, so it cannot be sent twice. */
  saving: boolean;
}

const emptyForm = (): PackForm => ({
  id: null,
  name: '',
  fromManagementPeriodId: null,
  toManagementPeriodId: null,
  showPrior: true,
  showBudget: true,
  showVariance: true,
  multiYearCount: 0,
  dimensionCode: null,
  sections: ['periodic-tb', 'variance'],
});

const sectionNames = (row: ManagementPackRow) =>
  row.sections.map((key) => PACK_SECTIONS.find((s) => s.key === key)?.name ?? key).join(', ');

/** Management report packs (Angular TaxreturnManagementPacksComponent). */
export function TaxreturnManagementPacks() {
  const navigate = useNavigate();
  const client = useSelectedClient();
  const [s, set, live] = useLiveState<State>({
    dataSource: [],
    periods: [],
    dimensions: [],
    showForm: false,
    form: emptyForm(),
    tried: false,
    saving: false,
  });

  useOnInit(() => {
    if (!guardClient(client, navigate)) {
      return;
    }
    managementReportingService.getManagementPeriods(client.id).then(
      (rows) => set({ periods: rows }),
      (error) => notifyError(error),
    );
    managementReportingService.getDimensions(client.id).then(
      (rows) => set({ dimensions: rows }),
      (error) => notifyError(error),
    );
    load();
  });

  function load(): void {
    if (!client) {
      return;
    }
    managementReportingService.getPacks(client.id).then(
      (rows) => set({ dataSource: rows }),
      (error) => notifyError(error),
    );
  }

  const setForm = (patch: Partial<PackForm>) => set((current) => ({ form: { ...current.form, ...patch } }));

  const onAdd = () => set({ form: emptyForm(), showForm: true, tried: false });

  const onEdit = (row: ManagementPackRow) =>
    set({
      form: {
        id: row.id,
        name: row.name,
        fromManagementPeriodId: row.fromManagementPeriodId,
        toManagementPeriodId: row.toManagementPeriodId,
        showPrior: row.showPrior,
        showBudget: row.showBudget,
        showVariance: row.showVariance,
        multiYearCount: row.multiYearCount,
        dimensionCode: row.dimensionCode,
        sections: [...row.sections],
      },
      showForm: true,
      tried: false,
    });

  const onCancel = () => set({ showForm: false, form: emptyForm(), tried: false });

  const isSectionSelected = (key: string) => s.form.sections.includes(key);

  function toggleSection(key: string, checked: boolean): void {
    set((current) => ({
      form: {
        ...current.form,
        sections: checked
          ? [...current.form.sections.filter((section) => section !== key), key]
          : current.form.sections.filter((section) => section !== key),
      },
    }));
  }

  function onSave(): void {
    if (!client) {
      notifyError('Open a client from Accounts first.');
      return;
    }

    const form = live.current.form;
    if (
      !form.name.trim() ||
      !form.fromManagementPeriodId ||
      !form.toManagementPeriodId ||
      form.sections.length === 0
    ) {
      set({ tried: true });
      validateForm(false);
      return;
    }

    set({ saving: true });
    managementReportingService
      .savePack({
        id: form.id,
        clientId: client.id,
        name: form.name.trim(),
        fromManagementPeriodId: form.fromManagementPeriodId,
        toManagementPeriodId: form.toManagementPeriodId,
        showPrior: form.showPrior,
        showBudget: form.showBudget,
        showVariance: form.showVariance,
        multiYearCount: Number(form.multiYearCount) || 0,
        dimensionCode: form.dimensionCode,
        sections: form.sections,
      })
      .then(
        () => {
          set({ saving: false });
          notifySuccess(NotificationTextMessage.successMessage);
          onCancel();
          load();
        },
        (error) => {
          set({ saving: false });
          notifyError(error);
        },
      );
  }

  function onDownload(row: ManagementPackRow): void {
    managementReportingService.downloadPack(row.id).then(undefined, (error) => notifyError(error));
  }

  async function onDelete(row: ManagementPackRow): Promise<void> {
    if (!(await confirmDelete('Report Pack'))) {
      return;
    }
    managementReportingService.deletePacks([row.id]).then(
      () => {
        notifySuccess(NotificationTextMessage.recordDeletedSuccessfully);
        load();
      },
      (error) => notifyError(error),
    );
  }

  const columns: DataTableColumn<ManagementPackRow>[] = [
    { id: 'name', header: 'Pack', cell: (row) => row.name },
    {
      id: 'range',
      header: 'Periods',
      headerClassName: 'w-270',
      cell: (row) => `${row.fromPeriodName} - ${row.toPeriodName}`,
    },
    { id: 'sections', header: 'Sections', cell: (row) => sectionNames(row) },
    { id: 'dimension', header: 'Dimension', headerClassName: 'w-150', cell: (row) => row.dimensionCode || 'All' },
    {
      id: 'updated',
      header: 'Updated',
      headerClassName: 'w-200',
      cell: (row) => formatDate(row.updatedAtUtc, 'DD/MM/YYYY HH:mm'),
    },
    {
      id: 'action',
      header: 'Action',
      headerClassName: 'w-150',
      cell: (row) => (
        <>
          <Tooltip title="Download PDF">
            <MatButton icon="picture_as_pdf" iconOutlined iconClassName="icon-color-pdf" onClick={() => onDownload(row)} />
          </Tooltip>
          <Tooltip title="Edit">
            <MatButton icon="edit" iconOutlined onClick={() => onEdit(row)} />
          </Tooltip>
          <Tooltip title="Delete">
            <MatButton icon="delete_outline" iconOutlined onClick={() => onDelete(row)} />
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <SideMenuPage menu="reports" contentClassName="tr-management-packs">
      <div className="content-body">
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <MatButton className="action-button primary-button" icon="add" onClick={onAdd}>
              <span>Report Pack</span>
            </MatButton>
          </div>
        </div>

        {s.showForm && (
          <div className="content-header pt-1 mb-0 hr-top">
            <div className="d-flex gap-20 align-items-center flex-wrap">
              <RequiredWrap>
                <InputField
                  fieldClassName="w-270"
                  maxLength={255}
                  placeholder="Pack name"
                  charMode="alphanumeric"
                  invalid={s.tried && !s.form.name.trim()}
                  value={s.form.name}
                  onChange={(event) => setForm({ name: event.target.value })}
                />
              </RequiredWrap>
              <RequiredWrap>
                <Select
                  className={`w-200${s.tried && !s.form.fromManagementPeriodId ? ' ng-invalid ng-touched' : ''}`}
                  items={s.periods}
                  bindLabel="name"
                  bindValue="id"
                  placeholder="From period"
                  value={s.form.fromManagementPeriodId}
                  onChange={(value) => setForm({ fromManagementPeriodId: value as string | null })}
                />
              </RequiredWrap>
              <RequiredWrap>
                <Select
                  className={`w-200${s.tried && !s.form.toManagementPeriodId ? ' ng-invalid ng-touched' : ''}`}
                  items={s.periods}
                  bindLabel="name"
                  bindValue="id"
                  placeholder="To period"
                  value={s.form.toManagementPeriodId}
                  onChange={(value) => setForm({ toManagementPeriodId: value as string | null })}
                />
              </RequiredWrap>
              <Select
                className="w-180"
                items={s.dimensions}
                bindLabel="name"
                bindValue="code"
                placeholder="All dimensions"
                value={s.form.dimensionCode}
                onChange={(value) => setForm({ dimensionCode: value as string | null })}
              />
              <InputField
                fieldClassName="w-100"
                inputMode="numeric"
                placeholder="Years"
                charMode="number"
                value={s.form.multiYearCount}
                onChange={(event) => setForm({ multiYearCount: event.target.value })}
              />
            </div>
            <div className="d-flex gap-20 align-items-center flex-wrap mt-1">
              <Checkbox checked={s.form.showPrior} onChange={(event) => setForm({ showPrior: event.target.checked })}>
                Prior period
              </Checkbox>
              <Checkbox checked={s.form.showBudget} onChange={(event) => setForm({ showBudget: event.target.checked })}>
                Budget
              </Checkbox>
              <Checkbox
                checked={s.form.showVariance}
                onChange={(event) => setForm({ showVariance: event.target.checked })}
              >
                Variance
              </Checkbox>
              {PACK_SECTIONS.map((section) => (
                <Checkbox
                  key={section.key}
                  checked={isSectionSelected(section.key)}
                  onChange={(event) => toggleSection(section.key, event.target.checked)}
                >
                  {section.name}
                </Checkbox>
              ))}
              <Button className="action-button primary-button" busy={s.saving} onClick={onSave}>
                Save
              </Button>
              <Button className="action-button secondary-button" onClick={onCancel}>
                Cancel
              </Button>
            </div>
            {s.tried && !s.form.name.trim() && <p className="text-danger mb-0 mt-1">Enter a name for the report pack.</p>}
            {s.tried && (!s.form.fromManagementPeriodId || !s.form.toManagementPeriodId) && (
              <p className="text-danger mb-0 mt-1">Select the management periods the pack starts from and ends at.</p>
            )}
            {s.tried && s.form.sections.length === 0 && (
              <p className="text-danger mb-0 mt-1">Select at least one section for the report pack.</p>
            )}
          </div>
        )}

        <div className="mat-table-wrapper">
          <DataTable stickyHeader columns={columns} rows={s.dataSource} rowKey={(row) => row.id} />
        </div>
      </div>
    </SideMenuPage>
  );
}
